import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, Share2, Users, MousePointerClick, Gift, Award, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";

export default function Refer() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) api.get("/referral/me").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  const link = stats ? `${window.location.origin}/?ref=${stats.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy the link");
    }
  };

  const share = async () => {
    const text = "Watch free TV on QTV Network — 13 channels of travel, fitness, music, pets & more.";
    if (navigator.share) {
      try {
        await navigator.share({ title: "QTV Network", text, url: link });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    copy();
  };

  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-[#030305] text-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 pt-40 text-center" data-testid="refer-signin-gate">
          <Gift className="w-12 h-12 text-[#FF2A2A] mx-auto mb-6" />
          <h1 className="font-display text-4xl uppercase tracking-tight mb-3">Invite Friends</h1>
          <p className="text-zinc-400 mb-8">Sign in to get your personal invite link and track your referrals.</p>
          <Link to="/login" className="inline-flex bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-8 py-3.5 rounded-sm font-bold transition-colors">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305] text-white" data-testid="refer-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-3">
            <Gift className="w-8 h-8 text-[#FF2A2A]" strokeWidth={2.5} />
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter">Invite Friends</h1>
          </div>
          <p className="text-zinc-400 mb-10 max-w-xl">
            Share your personal link. Every friend who opens it counts as a click, and every one who signs up is credited to you.
          </p>

          <div className="bg-[#0A0A0F] border border-white/10 rounded-md p-6 mb-8">
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Your invite link</label>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="refer-link-input"
                readOnly
                value={link}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-sm text-white font-mono truncate outline-none"
              />
              <div className="flex gap-3">
                <button
                  data-testid="refer-copy-btn"
                  onClick={copy}
                  className="flex items-center gap-2 bg-white text-black hover:bg-white/90 px-5 py-3 rounded-sm font-semibold transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
                </button>
                <button
                  data-testid="refer-share-btn"
                  onClick={share}
                  className="flex items-center gap-2 border border-white/20 hover:bg-white/10 px-5 py-3 rounded-sm font-semibold transition-colors"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0A0A0F] border border-white/10 rounded-md p-6" data-testid="refer-clicks-stat">
              <MousePointerClick className="w-6 h-6 text-zinc-400 mb-3" />
              <p className="font-display text-4xl tabular-nums">{stats ? stats.clicks : "—"}</p>
              <p className="text-xs uppercase tracking-wide text-zinc-500 mt-1">Link Clicks</p>
            </div>
            <div className="bg-[#0A0A0F] border border-white/10 rounded-md p-6" data-testid="refer-signups-stat">
              <Users className="w-6 h-6 text-[#FF2A2A] mb-3" />
              <p className="font-display text-4xl tabular-nums text-[#FF2A2A]">{stats ? stats.signups : "—"}</p>
              <p className="text-xs uppercase tracking-wide text-zinc-500 mt-1">Sign-ups</p>
            </div>
          </div>
        </motion.div>

        {stats && (() => {
          const threshold = stats.reward_threshold || 5;
          const earned = stats.badge_earned;
          const remaining = Math.max(0, threshold - stats.signups);
          const pct = Math.min(100, Math.round((stats.signups / threshold) * 100));
          return (
            <motion.div
              data-testid="refer-reward-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className={`mt-6 rounded-md p-6 border ${earned ? "border-[#FF2A2A]/50 bg-gradient-to-br from-[#1a0505] to-[#0A0A0F]" : "border-white/10 bg-[#0A0A0F]"}`}
            >
              <div className="flex items-center gap-4">
                <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center ${earned ? "bg-[#FF2A2A]" : "bg-white/5 border border-white/10"}`}>
                  {earned ? <Award className="w-7 h-7 text-white" strokeWidth={2.5} /> : <Lock className="w-6 h-6 text-zinc-500" />}
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Referral Reward</p>
                  <h3 className="font-display text-2xl uppercase tracking-tight mt-0.5" data-testid="refer-badge-name">
                    {earned ? (
                      <span className="text-[#FF2A2A]">QTV Ambassador</span>
                    ) : (
                      <span className="text-white">QTV Ambassador Badge</span>
                    )}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-0.5" data-testid="refer-badge-status">
                    {earned
                      ? "Unlocked — you're an official QTV Ambassador. Thanks for growing the network!"
                      : `Refer ${remaining} more ${remaining === 1 ? "friend" : "friends"} to earn your Ambassador badge.`}
                  </p>
                </div>
              </div>
              {!earned && (
                <div className="mt-5">
                  <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[#FF2A2A]"
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-2 tabular-nums">{stats.signups} / {threshold} sign-ups</p>
                </div>
              )}
            </motion.div>
          );
        })()}
      </div>
    </div>
  );
}
